import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://zmdktjragxwhmkkjdgcb.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InptZGt0anJhZ3h3aG1ra2pkZ2NiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDYxNTAwMzgsImV4cCI6MjA2MTcyNjAzOH0.qQi5khaB7Ox8xiuWRIyeEaT_kPHkQLZ6p5aHVCnjbKk';

let supabaseInstanceInternal = null;
let initializationPromise = null;

// Dynamic cookie domain to support both urban-mint.com and urbanmint.app
const getCookieDomain = () => {
  if (typeof window === 'undefined') return '.urban-mint.com'; // Default fallback
  
  const hostname = window.location.hostname;
  
  // Handle both urban-mint.com and urbanmint.app domains
  if (hostname === 'urbanmint.app' || hostname.endsWith('.urbanmint.app')) {
    return '.urbanmint.app';
  } else if (hostname === 'urban-mint.com' || hostname.endsWith('.urban-mint.com')) {
    return '.urban-mint.com';
  }
  
  // For localhost and other development environments, don't set domain
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')) {
    return '';
  }
  
  // Fallback to current hostname
  return `.${hostname}`;
};

// Cookie-based storage adapter so auth session works across subdomains
const cookieStorage = {
  getItem: (key) => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.split('; ').find(row => row.startsWith(`${key}=`));
    if (!match) return null;
    // Extract everything after the first '=' to handle values containing '=' characters (like JWT tokens)
    const value = match.substring(key.length + 1);
    return decodeURIComponent(value);
  },
  setItem: (key, value) => {
    if (typeof document === 'undefined') return;
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    
    // Only set Secure flag in HTTPS environments
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const secureFlag = isSecure ? '; Secure' : '';
    
    const cookieDomain = getCookieDomain();
    const domainFlag = cookieDomain ? `; domain=${cookieDomain}` : '';
    
    document.cookie = `${key}=${encodeURIComponent(value)}; path=/${domainFlag}; expires=${expires.toUTCString()}; SameSite=Lax${secureFlag}`;
  },
  removeItem: (key) => {
    if (typeof document === 'undefined') return;
    const cookieDomain = getCookieDomain();
    const domainFlag = cookieDomain ? `; domain=${cookieDomain}` : '';
    
    document.cookie = `${key}=; path=/${domainFlag}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
};

const initializeSupabase = () => {
  if (supabaseInstanceInternal) {
    return Promise.resolve(supabaseInstanceInternal);
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = new Promise((resolve, reject) => {
    if (supabaseUrl && supabaseAnonKey) {
      try {
        const clientOptions = {
          auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true,
            storage: cookieStorage,
            storageKey: 'urban-mint-auth',
          },
          global: {
            headers: { 'x-application-name': 'UrbanMintWebApp' },
          },
        };
        
        if (typeof window !== 'undefined' && typeof window.WebSocket !== 'undefined') {
          clientOptions.global.WebSocket = window.WebSocket;
        }

        supabaseInstanceInternal = createClient(supabaseUrl, supabaseAnonKey, clientOptions);
        
        resolve(supabaseInstanceInternal);
      } catch (error) {
        supabaseInstanceInternal = null;
        reject(error);
      }
    } else {
      supabaseInstanceInternal = null;
      reject(new Error('[SupabaseClient] Supabase URL and/or Anon Key are not configured. Supabase client cannot be initialized.'));
    }
  }).finally(() => {
    initializationPromise = null; // Reset promise state after completion/failure
  });

  return initializationPromise;
};

const getSupabase = () => {
  if (!supabaseInstanceInternal && !initializationPromise) {
    initializeSupabase().catch(err => {
      console.error('[SupabaseClient] Error during lazy initialization in getSupabase:', err.message);
    });
  }
  return supabaseInstanceInternal;
};

// Initialize on load
initializeSupabase().catch(error => {
  if (process.env.NODE_ENV === 'development') {
    console.error('[SupabaseClient] Initial auto-initialization failed:', error.message);
  }
  // TODO: Send error to error reporting service in production
});

const supabase = getSupabase(); 

const isSupabaseConnected = () => {
  const currentInstance = getSupabase();
  const connected = !!currentInstance;
  return connected;
};

export { supabase, getSupabase, initializeSupabase as ensureSupabaseInitialized, isSupabaseConnected };
  